/* ==========================================================================
   Panel Shell — generic overlay infrastructure.

   Owns open/close, scrim, Escape, focus containment, focus return, scroll
   locking and the one-panel-at-a-time rule. It contains no search, cart or
   filter logic; those surfaces supply content and reuse this behaviour.

   Progressive enhancement: with this module absent, triggers are ordinary
   links or buttons and navigation still works.
   ========================================================================== */

/* The scroll lock and the focus trap are shared with every other overlay in
   the theme — see `overlay.js` for why they are not defined here. */
import { scrollLock, trapTab, inertBackground, releaseBackground, closeOtherOverlays } from 'emore-overlay';

/* At most one global overlay is ever active — so there is never a second
   scrim, a second focus trap, or a second scroll lock. */
let activePanel = null;

class EmorePanel extends HTMLElement {
  connectedCallback() {
    this.surface = this.querySelector('[data-panel-surface]');
    this.scrim = this.querySelector('[data-panel-scrim]');
    this.trigger = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onScrim = this.onScrim.bind(this);

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-panel-close]')) this.close();
    });
    if (this.scrim) this.scrim.addEventListener('click', this.onScrim);
  }

  get isOpen() { return this.classList.contains('is-open'); }

  open(trigger) {
    if (this.isOpen) return;
    /* Closes any other panel AND the predictive overlay — see `overlay.js`. */
    closeOtherOverlays(this);

    this.trigger = trigger || document.activeElement;
    this.classList.add('is-open');
    this.removeAttribute('inert');
    activePanel = this;

    scrollLock.on();
    this.inerted = inertBackground(this.surface);
    document.addEventListener('keydown', this.onKeydown);

    /* Prefer a meaningful first control; fall back to the surface itself so
       focus can never remain on the obscured page behind. */
    const first = this.surface.querySelector('[data-panel-autofocus]')
      || this.surface.querySelector('[data-panel-close]')
      || this.surface;
    requestAnimationFrame(() => first.focus());

    this.dispatchEvent(new CustomEvent('panel:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    if (activePanel === this) activePanel = null;

    scrollLock.off();
    releaseBackground(this.inerted);
    this.inerted = null;
    document.removeEventListener('keydown', this.onKeydown);

    /* Focus returns to the exact element that opened the panel.
       preventScroll matters: focus() scrolls its target into view by default,
       which would immediately undo the scroll position just restored above and
       teleport the reader back to the header. */
    const t = this.trigger;
    this.trigger = null;
    if (t && document.contains(t)) t.focus({ preventScroll: true });

    this.dispatchEvent(new CustomEvent('panel:close', { bubbles: true }));
  }

  onScrim() { this.close(); }

  onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    trapTab(e, this.surface);
  }
}

if (!customElements.get('emore-panel')) {
  customElements.define('emore-panel', EmorePanel);
}

/* Focusing the clicked element is the default action of mousedown, and with a
   real pointer it settles after the panel has already moved focus inside
   itself — so the reader who just opened a dialog is left outside it, on the
   trigger. Suppressing that one default makes the panel's own focus
   authoritative. Keyboard activation is unaffected: it never fires mousedown,
   and closing still returns focus to the trigger explicitly. */
document.addEventListener('mousedown', (e) => {
  if (e.target.closest('[data-panel-open]')) e.preventDefault();
});

/* Delegated triggers: any element with data-panel-open="<panel id>". */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-panel-open]');
  if (!trigger) return;
  const panel = document.getElementById(trigger.getAttribute('data-panel-open'));
  if (!panel || typeof panel.open !== 'function') return;
  e.preventDefault();
  panel.open(trigger);
});

/* A panel left open across a breakpoint it no longer applies to would strand
   the scroll lock, so close it when its trigger stops being available. */
const desktop = window.matchMedia('(min-width: 1024px)');
desktop.addEventListener('change', (e) => {
  if (e.matches && activePanel && activePanel.dataset.closeOnDesktop !== undefined) {
    activePanel.close();
  }
});

export { EmorePanel };
